import React, { useEffect, useMemo, useState } from "react";
import {
  CAlert,
  CButton,
  CFormCheck,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import {
  MERITO_SECTIONS,
  applyMerito,
  readMeritoWorkbook,
  suggestEntityMapping,
} from "./meritoImport";

const describe = ({ created, updated, replaced, deleted, skipped }) =>
  [
    created && `${created} a criar`,
    updated && `${updated} a atualizar`,
    replaced && `${replaced} a substituir`,
    deleted && `${deleted} a apagar`,
    skipped && `${skipped} ignorados`,
  ]
    .filter(Boolean)
    .join(", ") || "Sem alterações";

/**
 * "Carregar Dados › Mérito" for the STEP I&D&I notices: the user maps the
 * entities of the file to the beneficiaries and picks the sections to import.
 */
const MeritoImportModal = ({
  workbook,
  config,
  formData,
  jsonData,
  onCancel,
  onApply,
}) => {
  const parsed = useMemo(() => {
    try {
      return { data: readMeritoWorkbook(workbook, config) };
    } catch (error) {
      return { error: error.message };
    }
  }, [workbook, config]);

  const [mapping, setMapping] = useState({});
  const [sections, setSections] = useState(
    () => new Set(MERITO_SECTIONS.map((section) => section.key)),
  );

  useEffect(() => {
    if (parsed.data) {
      setMapping(
        suggestEntityMapping(parsed.data.entities, formData?.beneficiaries),
      );
    }
  }, [parsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = useMemo(
    () =>
      parsed.data
        ? applyMerito(formData, parsed.data, { sections, mapping }, jsonData, config)
        : null,
    [parsed, formData, sections, mapping, jsonData, config],
  );

  const toggleSection = (key) =>
    setSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const usedBeneficiaries = new Set(Object.values(mapping).filter(Boolean));

  return (
    <CModal size="xl" visible onClose={onCancel} scrollable>
      <CModalHeader>
        <CModalTitle>Importar ficheiro de Mérito</CModalTitle>
      </CModalHeader>

      <CModalBody>
        {parsed.error ? (
          <CAlert color="danger">{parsed.error}</CAlert>
        ) : (
          <>
            <p className="text-muted">
              Associe cada entidade do ficheiro a um beneficiário e escolha o
              que pretende importar. O ficheiro prevalece sobre o formulário:
              o que não estiver no ficheiro é apagado (atividades, CAE e os
              técnicos, linhas de equipa e custos dos beneficiários
              importados). Das linhas existentes aproveitam-se apenas os
              campos que o ficheiro não preenche.
            </p>

            <label className="fw-bold mb-2">Entidades</label>
            <CTable bordered small responsive className="mb-4">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Entidade no ficheiro</CTableHeaderCell>
                  <CTableHeaderCell>Tipologia</CTableHeaderCell>
                  <CTableHeaderCell style={{ width: "40%" }}>
                    Beneficiário
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {parsed.data.entities.map((entity) => (
                  <CTableRow key={entity.index}>
                    <CTableDataCell>{entity.name}</CTableDataCell>
                    <CTableDataCell className="text-muted">
                      {entity.type}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormSelect
                        size="sm"
                        value={mapping[entity.index] ?? ""}
                        onChange={(event) =>
                          setMapping((prev) => ({
                            ...prev,
                            [entity.index]: event.target.value,
                          }))
                        }
                      >
                        <option value="">Não importar</option>
                        {(formData?.beneficiaries ?? []).map((ben) => (
                          <option
                            key={ben.id}
                            value={String(ben.id)}
                            disabled={
                              usedBeneficiaries.has(String(ben.id)) &&
                              mapping[entity.index] !== String(ben.id)
                            }
                          >
                            {ben.name || `Beneficiário ${ben.id}`}
                          </option>
                        ))}
                      </CFormSelect>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>

            <label className="fw-bold mb-2">O que importar</label>
            <CTable bordered small responsive>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell style={{ width: 50 }} />
                  <CTableHeaderCell>Secção</CTableHeaderCell>
                  <CTableHeaderCell>Resumo</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {MERITO_SECTIONS.map((section) => (
                  <CTableRow key={section.key}>
                    <CTableDataCell>
                      <CFormCheck
                        checked={sections.has(section.key)}
                        onChange={() => toggleSection(section.key)}
                        aria-label={section.label}
                      />
                    </CTableDataCell>
                    <CTableDataCell>{section.label}</CTableDataCell>
                    <CTableDataCell className="text-muted">
                      {sections.has(section.key)
                        ? describe(preview.summary[section.key])
                        : "-"}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>

            {preview.warnings.length > 0 && (
              <CAlert color="warning" className="small mb-0">
                <div className="fw-bold mb-1">Avisos</div>
                <ul className="mb-0">
                  {[...new Set(preview.warnings)].map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </CAlert>
            )}
          </>
        )}
      </CModalBody>

      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onCancel}>
          Cancelar
        </CButton>
        <CButton
          color="primary"
          disabled={!!parsed.error || sections.size === 0}
          onClick={() =>
            onApply((prev) =>
              applyMerito(prev, parsed.data, { sections, mapping }, jsonData, config)
                .formData,
            )
          }
        >
          Importar selecionados
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

/** importFiles entry of a notice: opens the modal instead of importing directly. */
export const meritoImport = (config) => ({ Modal: MeritoImportModal, config });

export default MeritoImportModal;
